import React from "react";
import {NavLink} from "react-router-dom";
import {format} from "date-fns";

// Models
import ICarouselContentDTO from "../../../Models/DTOs/ICarouselContentDTO";
import {AuthUser} from "../../../Models/DTOs/AuthUser";

// Helpers
import {GetContentRedirect} from "../../../Helpers/Content/Content";
import {HasLifetimeMembership, IsSubscriptionActive} from "../../../Helpers/Subscription";
import {IsFreeAccount} from "../../../Helpers/Account/Admin";

type CarouselLandscapeProps = {
    item: ICarouselContentDTO;
    userData: AuthUser | Record<string, never> | null;
    authTokenExists: boolean;
    itemWidth?: number;
    isLoading: boolean;
};

function safeParseDate(dateString: string) {
    if (!dateString) return null;

    const s = dateString.trim();

    // If it already includes timezone info, parse as-is.
    const hasTZ = /[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s);

    const normalized = hasTZ ? s : `${s}Z`;

    const dateObj = new Date(normalized);
    return isNaN(dateObj.getTime()) ? null : dateObj;
}

function processItem(item: ICarouselContentDTO) {
    const
        now = new Date(),
        releaseDateObj = safeParseDate(item.ReleaseDate),
        earlyAccessDateObj = safeParseDate(item.ReleaseDateEarlyAccess),
        isNotReleased = !releaseDateObj || releaseDateObj > now,
        earlyAccessAvailable =
            !!earlyAccessDateObj &&
            earlyAccessDateObj < now &&
            !!releaseDateObj &&
            releaseDateObj > now &&
            item.Text === "Film",
        releaseDate = isNotReleased ? "" : format(earlyAccessDateObj ?? releaseDateObj, "dd/MM/yyyy hh:mm a");

    return {
        isNotReleased,
        earlyAccessAvailable,
        releaseDate,
        releaseDateObj,
    };
}

const LandscapeContentItem: React.FC<CarouselLandscapeProps> = (
    {
        item,
        userData,
        authTokenExists,
        itemWidth,
        isLoading,
    }
) => {
    const
        {earlyAccessAvailable, releaseDateObj, isNotReleased} = processItem(item),
        userHasLifetimeAccessLevel = HasLifetimeMembership(userData?.subscription?.plan_id),
        isDisabled = isNotReleased && !(earlyAccessAvailable && userHasLifetimeAccessLevel),
        redirect = !isDisabled ? GetContentRedirect(item.ContentType, item.Id) : null;

    //console.log(item.Thumbnail);

    return (
        <div
            id={`content--${item.Id}`}
            className={`
                carousel-landscape__item u-relative_hidden
                ${earlyAccessAvailable && userHasLifetimeAccessLevel ? "has-early-access" : "has-no-early-access"}
                ${isDisabled ? "disable-cursor" : "normal-cursor"}
            `}
            style={{width: itemWidth ? `${itemWidth}px` : "100%"}}
        >
            {
                item.IsFreeView &&
                (!authTokenExists || (!IsSubscriptionActive(userData?.subscription?.status) && !userData?.subscription?.is_entitled)) &&
                !userHasLifetimeAccessLevel &&
                !IsFreeAccount(userData?.admin_role_id) && (
                <div className="carousel-landscape__item-label free-view-content-item-label">
                    <span>Watch Free</span>
                </div>
            )}

            {earlyAccessAvailable && userHasLifetimeAccessLevel && (
                <div className="carousel-landscape__item-label free-view-content-item-label">
                    <span>Early Access</span>
                </div>
            )}

            {earlyAccessAvailable && !userHasLifetimeAccessLevel && (
                <div className="carousel-landscape__item-label locked-content-item-label">
                    <span>Coming {releaseDateObj ? format(releaseDateObj, "dd/MM/yyyy hh:mm a") : ""}</span>
                </div>
            )}

            {isLoading ? (
                <div className="loader--side-to-side is-sixteen-nine-aspect-ratio"/>
            ) : (
                <NavLink to={redirect ? redirect : "#"} className="is-sixteen-nine-aspect-ratio">
                    {!isLoading && releaseDateObj && releaseDateObj > new Date() && (
                        <div className="carousel-landscape__item-label locked-content-item-label">
                            <span>Releases {format(releaseDateObj, "dd/MM/yyyy hh:mm a")}</span>
                        </div>
                    )}

                    <div
                        className="u-full_cover_absolute"
                        style={{
                            background: `url("${item.Thumbnail ?? ""}") center/cover no-repeat`,
                        }}
                    />
                </NavLink>
            )}

            {isLoading ? (
                <p className="carousel-landscape__item-title"/>
            ) : (
                <NavLink to={redirect ? redirect : "#"} className="carousel-landscape__item-title">
                    <p>{item.Title}</p>
                </NavLink>
            )}
        </div>
    );
};

export default LandscapeContentItem;
